// Nachschlage-Inhalte, aus der bisherigen SystemPage uebernommen (Stand Juli 2026).
// Architektur am 19.10.2026 an die Aenderungen der Aufraeum-Phase angepasst.
export type Strategy = {
  name: string;
  label: string;
  emoji: string;
  short: string;
  description: string;
  calc: string;
  entry: string;
  timeframe: string;
  tags: string[];
  cluster?: string;
};
export const INTRADAY: Strategy[] = [
  {
    name: "ORB", label: "Opening Range Breakout", emoji: "🚀",
    short: "Ausbruch aus den ersten 3 × 15min-Kerzen",
    description: "Opening Range = High/Low der ersten 3 abgeschlossenen 15min-Kerzen. Ausbruch wenn aktuelle Kerze über/unter Range schließt. Breakout-Stärke > 0.5× ATR gibt Bonus.",
    calc: "Basis 35 + Vol>1.8x→+25 / Vol>1.3x→+15 + ADX>20→+15 + Breakout>0.5×ATR→+15. Max: 90.",
    entry: "Schlusskurs über/unter Opening Range mit Volumen",
    timeframe: "15min · EU 10:00–10:30 / US 16:00–17:00",
    tags: ["ORB", "Breakout", "Opening"],
  },
  {
    name: "Gap_Go", label: "Gap & Go", emoji: "⚡",
    short: "Tages-Gap ≥ 1% als Momentum nutzen",
    description: "Echter Tages-Gap (Daily Open vs. Previous Close) ≥ 1%. Erste 15min-Kerze bestätigt Gap-Richtung. v5: Freshness-Gate prüft aktuellen Kurs — blockiert bei Gap-Fill oder >0.5% Retrace. Entry = aktueller Kurs statt Kerzen-Close.",
    calc: "Basis 35 + Gap-Größe×5 (max +15) + Vol>2.0x→+25 / Vol>1.5x→+15 + First Candle OK→+20. Max: 95.",
    entry: "Aktueller Kurs bei bestätigtem Momentum (Freshness-Gate)",
    timeframe: "15min · EU 10:00–10:15 / US 16:00–16:30",
    tags: ["Gap", "Momentum", "Freshness-Gate"],
  },
  {
    name: "VWAP_Bounce", label: "VWAP Bounce", emoji: "🏀",
    short: "Trend-Fortsetzung am Rolling-VWAP",
    description: "Preis zieht an Rolling-VWAP zurück (< 0.3 ATR Distanz). Trend-Richtung via VWAP vs. EMA50. Bestätigung durch Reversal-Kerze oder Candlestick-Pattern. v5: RSI session-übergreifend berechnet (kein 50.0-Fallback mehr).",
    calc: "Basis 35 + Pattern→+20 / Reversal-Kerze→+10 + Vol>1.2x→+15 + Distanz<0.15 ATR→+15. Max: 85.",
    entry: "Reversal-Kerze oder Candlestick-Pattern direkt am VWAP",
    timeframe: "15min · EU 10:00–17:30 / US 16:30–22:00",
    tags: ["VWAP", "Bounce", "Trend"],
  },
  {
    name: "Pullback_EMA", label: "Pullback to EMA", emoji: "↩️",
    short: "EMA9-Bounce mit EMA20-Trendfilter",
    description: "Preis zieht auf EMA9 zurück während EMA20 den übergeordneten Trend bestätigt. Entry-Drift-Limit 0.2% verhindert Einstiege bei bereits weggelaufenem Kurs. Seit v3.5.",
    calc: "Basis 35 + EMA9-Nähe + EMA20-Trend→+20 + Vol-Bestätigung→+15 + ADX→+15. Max: ~90.",
    entry: "Bounce an EMA9 mit EMA20-Trendfilter, Entry-Drift ≤ 0.2%",
    timeframe: "15min · EU 10:30–17:30 / US 16:30–22:00",
    tags: ["EMA9", "Pullback", "Trend"],
  },
  {
    name: "Range_Trading", label: "Range Trading", emoji: "📏",
    short: "Session-Range-Bounce bei Seitwärtsmarkt",
    description: "Identifiziert Session-Ranges (≥ 2h, < 1.5% Breite) und handelt Bounces an den Rändern. Mindestens 2 Touches je Rand für Validierung. Seit v3.5.",
    calc: "Basis 35 + Range-Qualität (Touches, Breite) + RSI-Bestätigung + Vol. Max: ~85.",
    entry: "Bounce an Range-Rand mit ≥ 2 vorherigen Touches",
    timeframe: "15min · EU 11:00–17:00 / US 17:00–21:30",
    tags: ["Range", "Seitwärts", "Bounce"],
  },
];

export const SWING: Strategy[] = [
  // --- Cluster: Trend ---
  {
    name: "Pullback_Trend", label: "Pullback im Trend", emoji: "↩️", cluster: "Trend",
    short: "Rücksetzer in EMA20/50/200-Trendstruktur",
    description: "EMA20 > EMA50 > EMA200 (Uptrend) oder umgekehrt. Preis zieht auf 0.8% Nähe zu EMA20 oder EMA50 zurück und dreht. RSI 35–50 (Long) / 50–65 (Short). RS-Ratio > 3 gibt Bonus.",
    calc: "Basis 55 + RSI optimal→+15 + Vol>1.3x→+15 + Pattern→+15 + RS>3→+5. Max: 100.",
    entry: "Pullback an EMA20 oder EMA50 mit RSI-Abkühlung",
    timeframe: "Daily", tags: ["Pullback", "EMA", "Trend", "RS"],
  },
  {
    name: "ADX_Continuation", label: "ADX Continuation", emoji: "📈", cluster: "Trend",
    short: "Starker Trend (ADX ≥ 25) setzt sich fort",
    description: "ADX ≥ 25 signalisiert starken Trend. +DI > -DI = Long, umgekehrt Short. Preis über/unter EMA50 als zusätzlicher Trendfilter.",
    calc: "Basis 35 + ADX>30→+30 / ADX>25→+20 + EMA50-Trend→+20 + Bonus→+15 + RS>3→+5. Max: 100.",
    entry: "In Trendrichtung bei Rücksetzer oder Continuation",
    timeframe: "Daily", tags: ["ADX", "Trend", "Continuation"],
  },
  {
    name: "EMA_Cross_Daily", label: "EMA Cross Daily", emoji: "✂️", cluster: "Trend",
    short: "EMA9/21 Cross auf Tagesbasis",
    description: "EMA9 kreuzt EMA21 auf Daily-Bars. ADX und Volumen als Bestätigung. Braucht zweiten Cluster (Confluence) da Score-Basis niedriger.",
    calc: "Basis 35 + ADX>25→+20 / ADX>20→+10 + Vol>1.3x→+20. Max: 75.",
    entry: "Am Tag des Crosses oder beim ersten Pullback",
    timeframe: "Daily", tags: ["EMA", "Crossover", "Daily"],
  },
  // --- Cluster: Breakout ---
  {
    name: "Breakout_Range", label: "Range Breakout", emoji: "💥", cluster: "Breakout",
    short: "Ausbruch aus 10-Bar-Konsolidierung",
    description: "10-Bar-Range (High/Low der letzten 10 Tage) muss ≤ 3× ATR sein. Ausbruch über Range-High (Long) oder unter Range-Low (Short). Volumen > 2× für starkes Signal.",
    calc: "Basis 50 + Vol>2.0x→+30 / Vol>1.5x→+15 + EMA50-Trend→+20. Max: 100.",
    entry: "Schlusskurs über/unter Range mit Volumen",
    timeframe: "Daily", tags: ["Breakout", "Range", "Volume"],
  },
  {
    name: "BB_Squeeze", label: "Bollinger Band Squeeze", emoji: "🗜️", cluster: "Breakout",
    short: "Volatilitätskompression vor Ausbruch",
    description: "BB-Breite ≤ 110% des 120-Tage-Minimums. Ausbruch über obere/unter untere BB signalisiert Ende der Kompression.",
    calc: "Basis 60 + Vol>1.5x→+20 + EMA50-Trend→+20. Max: 100.",
    entry: "Erste Kerze die die BB nach Squeeze verlässt",
    timeframe: "Daily", tags: ["Bollinger", "Squeeze", "Volatility"],
  },
  {
    name: "Volume_Breakout", label: "Volume Breakout", emoji: "📦", cluster: "Breakout",
    short: "Vol ≥ 2× + Ausbruch über 20-Tage-High",
    description: "Volumen ≥ 2× 20-Tage-Durchschnitt kombiniert mit Preisausbruch über 20-Tage-High (Long) oder unter 20-Tage-Low (Short).",
    calc: "Basis 55 + Vol>3.0x→+35 / Vol>2.0x→+25 + Schlusskurs bestätigt→+15. Max: 100.",
    entry: "Intraday-Bestätigung des Ausbruchs",
    timeframe: "Daily", tags: ["Volume", "Breakout", "Institutional"],
  },
  {
    name: "Week52_Approach", label: "52-Wochen Approach", emoji: "🏆", cluster: "Breakout",
    short: "Preis nähert sich 52W-High/Low (< 3%)",
    description: "Preis nähert sich dem 52-Wochen-Hoch/-Tief auf < 3% Distanz. Je näher desto höher der Score. Braucht zweiten Cluster (Confluence).",
    calc: "Basis 35 + Distanz<1%→+25 / <2%→+18 / <3%→+10 + Vol>1.3x→+20. Max: 80.",
    entry: "Beim Annähern mit Volumen, vor dem eigentlichen Ausbruch",
    timeframe: "Daily", tags: ["52W-High", "Momentum", "Breakout"],
  },
  {
    name: "Gap_Go_Swing", label: "Gap & Go Swing", emoji: "⚡", cluster: "Breakout",
    short: "Tages-Gap 0.5–5% als Swing-Momentum",
    description: "Daily-Gap 0.5–5% in Gap-Richtung gehandelt. Bestätigung durch Tagesschlusskurs. EMA50-Filter sichert Trendrichtung.",
    calc: "Basis 35 + Gap 1-3%→+25 + Schlusskurs bestätigt→+20 + EMA50-Trend→+15. Max: 95.",
    entry: "Tagesschluss in Gap-Richtung",
    timeframe: "Daily", tags: ["Gap", "Momentum", "Daily"],
  },
  // --- Cluster: Mean-Reversion ---
  {
    name: "RSI_Divergence", label: "RSI Divergenz Swing", emoji: "🔀", cluster: "Mean-Reversion",
    short: "Higher High / Lower Low mit RSI-Divergenz",
    description: "Bear: Preis macht Higher High vs. 10 Tage zuvor, RSI macht Lower High. Bull umgekehrt. Pattern-Bestätigung (Hammer, Engulfing) gibt Bonus.",
    calc: "Basis 60 + Candlestick-Pattern→+20 + Vol>1.2x→+20. Max: 100.",
    entry: "Nach Bestätigung der Divergenz durch Reversal-Kerze",
    timeframe: "Daily", tags: ["RSI", "Divergenz", "Reversal"],
  },
  {
    name: "Hammer_Engulfing", label: "Hammer / Engulfing", emoji: "🔨", cluster: "Mean-Reversion",
    short: "Candlestick-Pattern an EMA200 Key Level",
    description: "Hammer, Shooting Star, Bullish/Bearish Engulfing. Extra-Bonus wenn Preis < 1.5% von EMA200 entfernt (Key Level).",
    calc: "Basis 60 + Vol>1.3x→+20 + Preis < 1.5% von EMA200→+20. Max: 100.",
    entry: "Nach Schließen der Bestätigungskerze an Key Level",
    timeframe: "Daily", tags: ["Candlestick", "Pattern", "Key Level"],
  },
  // --- Cluster: Pre-Earnings Vega ---
  {
    name: "Earnings_Vega", label: "Pre-Earnings Vega", emoji: "📅", cluster: "Pre-Earnings Vega",
    short: "IV-Anstieg 7–21 Tage vor Earnings nutzen (nur OS)",
    description: "Entry 7–21 Tage vor Earnings, Sweet Spot 8–16 Tage (+15 Bonus). HV-Perzentile als IV-Proxy. EPS-Estimate vorhanden gibt +5. Force-Close bei ≤ 2 Tagen vor Earnings. NUR mit Optionsscheinen — KO + Earnings-Gap = Knock-out-Risiko.",
    calc: "Sweet Spot 8-16d→+15 + HV Percentile + EPS-Estimate→+5. Gewichtet in allen Regimes.",
    entry: "7–21 Tage vor Earnings, idealerweise bei niedrigem HV-Perzentil",
    timeframe: "Daily", tags: ["Earnings", "Vega", "IV", "Nur OS"],
  },
];

export const LONGTERM: Strategy[] = [
  // --- Cluster: Trend ---
  {
    name: "MA200", label: "MA200 Trend", emoji: "📏", cluster: "Trend",
    short: "Preis über/unter steigendem/fallendem MA200",
    description: "MA200 als wichtigster Langfrist-Indikator. Preis > MA200 + MA200 steigt = Bullish. Near-MA (< 3% Distanz) gibt höheren Basis-Score. RSI als Timing-Filter.",
    calc: "Basis 45 (near<3%) / 35 (far) + MA-Richtung→+25 + Vol>1.2x→+20 + RSI optimal→+20. Max: 100.",
    entry: "Pullback zum MA200 oder Momentum-Einstieg",
    timeframe: "Daily / Weekly", tags: ["MA200", "Longterm", "Trend"],
  },
  {
    name: "Golden_Cross", label: "Golden Cross / Death Cross", emoji: "✨", cluster: "Trend",
    short: "MA50 kreuzt MA200 nach oben/unten",
    description: "Golden Cross (MA50 > MA200): klassisches Bullensignal. Death Cross umgekehrt. MA200-Trendrichtung über 10 Tage bestätigt Qualität.",
    calc: "Basis 35 + MA200-Richtung→+25 + Vol>1.3x→+25 / Vol>1.0x→+12. Max: 85.",
    entry: "Beim Cross oder ersten Pullback danach",
    timeframe: "Daily / Weekly", tags: ["MA50", "MA200", "Cross"],
  },
  // --- Cluster: Momentum ---
  {
    name: "Momentum_90", label: "90-Tage Momentum", emoji: "🏃", cluster: "Momentum",
    short: "≥ 10% Performance über 90 Tage + 30T-Bestätigung",
    description: "Aktien mit ≥ 10% Performance über 90 Tage tendieren zur Fortsetzung (Momentum-Anomalie). 30-Tage-Trend muss gleiche Richtung zeigen. RSI < 75 verhindert Übertreibung.",
    calc: "Basis 35 + Perf>20%→+30 / >15%→+21 / >10%→+12 + RSI OK→+15. Max: 80.",
    entry: "Nach kurzem Rücksetzer (< 10% vom 90T-Hoch)",
    timeframe: "Daily / Weekly", tags: ["Momentum", "90-Day", "Relative Strength"],
  },
  {
    name: "Week52_High", label: "52-Wochen Ausbruch", emoji: "🏆", cluster: "Momentum",
    short: "Preis ≥ 99.9% des 52W-Hochs/Tiefs",
    description: "Neue 52-Wochen-Hochs sind statistisch bullisch. Vorherige Kerze muss bereits nahe dem Level gewesen sein. Oft Start langfristiger Trendbewegungen.",
    calc: "Basis 35 + Vol>1.5x→+30 / Vol>1.2x→+18 + Vorherige Kerze bestätigt→+15. Max: 80.",
    entry: "Beim Ausbruch über 52W-Hoch mit Volumen",
    timeframe: "Daily / Weekly", tags: ["52W-High", "Breakout", "Momentum"],
  },
];

export const MODE_META = {
  intraday: {
    label: "⚡ Intraday", strategies: INTRADAY,
    threshold: "Score ≥ 60", sl: "1.5× ATR", tp: "2.5× ATR", rrr: "min. 1:1.5",
    regime: "Intraday-Regime: −1% Hard-SL, +1.5% TSL-Trigger, 1% Trail, TP 2.5×Risk, Session-Force-Close",
  },
  swing: {
    label: "📈 Swing", strategies: SWING,
    threshold: "Score ≥ 55 (Confluence: 2+ Cluster)", sl: "1.5× ATR", tp: "3.5× ATR", rrr: "min. 1:2.0",
    regime: "Parallel-Tracking: Standard (−50% SL) · Safety (−5% SL) · Conservative (−15% SL)",
  },
  longterm: {
    label: "🌍 Longterm", strategies: LONGTERM,
    threshold: "Score ≥ 55", sl: "2.0× ATR", tp: "6.5× ATR", rrr: "min. 1:3.0",
    regime: "Parallel-Tracking wie Swing (Standard · Safety · Conservative)",
  },
} as const;
export const CHANGELOG = [
  {
    version: "6.0", date: "14.–19. Okt 2026", current: true, items: [
      "Aufräumen: Trade-Manager, Signal-Archiv, Health-Tracker, Daily Report, Journal-Kursupdate, Intraday-Alert, VDAX-Fetch und TradingView-Webhook abgeschaltet; Tracking-Daten als Dump archiviert, Tabellen gelöscht",
      "Flux, Piscator und alte Units/Dateien entfernt (Archiv lokal + Hetzner)",
      "Scanner v3: 15-Min-Takt mit Live-Kerze, Zwei-Läufe-Regel, Asien/ASX-Tagesdaten, Hinweis 'gegen Markttrend' statt Bärenmarkt-Filter",
      "Watchdog v3: prüft Funktionen, startet neu, Telegram nur bei Zustandswechsel",
      "Login mit Session-Token, alle Endpoints geschützt, API nur noch auf 127.0.0.1",
      "Instrumente bereinigt: Symbole umgestellt, Splits neu geladen, 20 ohne Yahoo-Daten deaktiviert",
      "Redesign: 4 Seiten (Scanner, Journal, Instrumente, System), Geist-Schrift, Hell/Dunkel serverseitig gespeichert",
      "Scanner-Filter je Modus serverseitig, Ablage für Signale, Journal mit Ablage/Auswertung/CSV für Claude",
    ],
  },
  {
    version: "5.0", date: "09.–14. Jul 2026", current: false, items: [
      "Cortex/SIGMUND ML-Layer komplett archiviert (archive/cortex_2026/) — 6 Monate ohne messbare Signal-Verbesserung",
      "Training-Cron deaktiviert, ML-API-Endpoints + Frontend-ML-UI entfernt, win_prob-Anzeigen entfernt (war synthetische Formel)",
      "Signal-Bugfix: RSI session-übergreifend berechnet (kein 50.0-Fallback mehr bei Intraday)",
      "Signal-Bugfix: Gap & Go Freshness-Gate (TKMS-Fall — Signal feuerte >1h verspätet mit gefülltem Gap)",
      "Signal-Bugfix: Intraday-Stale-Bug behoben (DELETE vor jedem Zyklus, keine toten Signale nach Session-Ende)",
      "Theme-System: CSS-Variablen-Tokens, Light/Dark-Toggle (localStorage), Kontrast-Fix",
      "4-Regime-Tracking: regime_trades-Tabelle, Standard/Safety/Conservative + Intraday parallel",
      "Signal-Close per Mehrheitsentscheid + Ø-PnL der Regimes",
      "Shadow-TSL-System + scharfe Closes stillgelegt (Regime-System ersetzt beide)",
      "Frischer Start: alle Tracking-Tabellen geleert (pre_v5-Dump gesichert)",
      "RegimeTrades-Frontend: 4 Stat-Boxen + Regime-Spalten-Tabelle",
      "Offsite-Backup: rclone + WebDAV → Hetzner Storage Share, täglich 23:15, 14 Tage Rotation",
      "Journal-Labels: SIGMUND → Nexus/Signal Score",
    ],
  },
  {
    version: "3.5.0", date: "13. Jul 2026", current: false, items: [
      "Pullback to EMA + Range Trading als Intraday-Strategien 4 und 5",
      "Intraday-Telegram-Alert via systemd Timer (10:05 + 16:20 Europe/Berlin)",
      "Cron → systemd-Timer für zeitkritische Jobs (TZ-Bug: Cron-Daemon ignoriert TZ= auf diesem Server)",
    ],
  },
  {
    version: "3.4.7", date: "30. Jun 2026", current: false, items: [
      "quality_flag-Filter für Signal-Qualitätskontrolle",
      "PnL-Cap bei −100% (verhindert statistische Verzerrung)",
      "Telegram-Outlier-Alert bei extremen PnL-Werten",
    ],
  },
  {
    version: "3.4.6", date: "26. Jun 2026", current: false, items: [
      "Conservative Shadow Policy implementiert",
    ],
  },
  {
    version: "3.4.5", date: "22. Jun 2026", current: false, items: [
      "Earnings Phase 1 live: earnings_calendar, fetch_earnings.py (Alpha Vantage Bulk + yfinance Fallback)",
      "1029 von 1472 Symbolen mit Earnings-Daten",
      "Earnings-Tag in allen Engines, farbcodiertes Frontend-Badge",
      "Pre-Earnings Vega Swing-Strategie als 4. Cluster in swing.py",
      "Force-Close bei days_until ≤ 2 im Trade Manager",
    ],
  },
  {
    version: "3.4.4", date: "21. Jun 2026", current: false, items: [
      "Bucket-Bug behoben: evaluate_new_signals() setzte bucket = signal_type statt {region}_{signal_type}",
      "105.008 bestehende Trades per Backfill korrigiert",
      "Health Score Korrelation validiert: ≥70 → Ø +7.15% | <40 → Ø −5.06%",
    ],
  },
  {
    version: "3.4.0–3.4.3", date: "18.–21. Jun 2026", current: false, items: [
      "Intraday Scanner v1.0: 3 Strategien (ORB, Gap & Go, VWAP Bounce)",
      "Intraday Trade Manager: SL/TP auf 15min-Bars, Force-Close EU 17:15 / US 21:45",
      "Quality Filters: VWAP Bounce Score-Abzüge (RVOL, RRR)",
      "tsl_version-Spalte in signal_outcomes + tsl_shadow",
    ],
  },
  {
    version: "3.3.x", date: "Jun 2026", current: false, items: [
      "Shadow TSL System: tsl_shadow Tabelle, Trade Manager Integration",
      "OutcomePage: 4 Tabs (Outcomes / ML-Engine / SIGMUND Trades / Shadow TSL)",
      "Server-Rückmigration auf CPX23, Overextension-Feature bei Migration verloren",
    ],
  },
  {
    version: "3.2.x", date: "Mai–Jun 2026", current: false, items: [
      "Trade Health Tracker: täglich nach EU + US Schluss, Health Score 0–100",
      "ml_regime: Regime-Detection (ADX + ATR-Perzentile, 4 Klassen)",
      "Scanner Kachel-Redesign, Confluence-Badge, Firewall (UFW)",
    ],
  },
  {
    version: "3.0–3.1", date: "Mär–Apr 2026", current: false, items: [
      "Swing-Engine v3: SCORE_THRESHOLD 70, Confluence-Pflicht (2 Cluster)",
      "SIGMUND Trade Manager: TSL-Überwachung + Outcome-Tracking",
      "Two-Layer XGBoost + Meta-Learner, Journal v2",
      "IV-Tag: HV20 52W-Perzentile als OS-Prämien-Indikator",
    ],
  },
  {
    version: "2.x", date: "Feb–Mär 2026", current: false, items: [
      "Journal v2: Tranchen, Asset-Klassen, P&L-Auswertung",
      "Backtest Engine, ML Pipeline v1, TimescaleDB Migration",
      "1.434 Instrumente aus 17 Ländern + Rohstoffe",
    ],
  },
  {
    version: "1.0", date: "Feb 2026", current: false, items: [
      "Grundsystem: FastAPI + React + TimescaleDB",
      "HTTPS, systemd Services, Telegram Alerts",
    ],
  },
];

export const MARKETS = [
  { region: "USA",           detail: "S&P 500 (~400), NASDAQ 100 (88), NYSE Composite" },
  { region: "Deutschland",   detail: "DAX 40, MDAX 48, SDAX 50" },
  { region: "Europa",        detail: "FTSE100, CAC40, IBEX35, AEX, BEL20, OMXS30, SMI, FTSEMIB, ATX, OMXH25, OMXC25" },
  { region: "Asien/Pazifik", detail: "NIKKEI225, HSI, KOSPI, NIFTY50, ASX200" },
  { region: "Amerika",       detail: "TSX60, IBOVESPA" },
  { region: "Rohstoffe",     detail: "Gold, Silber, Platin, Palladium, WTI, Brent, Erdgas, Weizen, Mais, Soja" },
  { region: "Volatilität",   detail: "VIX, VDAX, VSTOXX, VXN, RVX, VFTSE, JNIV" },
];

export const ARCHITECTURE = [
  { label: "Backend",    value: "FastAPI (Python 3.12), systemd: trading-api, nur 127.0.0.1:8000" },
  { label: "Frontend",   value: "React + TypeScript, Production-Build über nginx" },
  { label: "Datenbank",  value: "TimescaleDB (PostgreSQL 16), Hypertables bars_*" },
  { label: "Scanner",    value: "Python-Daemon, Swing/Intraday alle 15 Min, Longterm 2× täglich, systemd: trading-scanner" },
  { label: "Marktdaten", value: "yfinance (Daily + 15 Min) über trading-scheduler" },
  { label: "Watchdog",   value: "alle 5 Min, Neustart bei Ausfall, Telegram nur bei Zustandswechsel" },
  { label: "Login",      value: "Session-Token (30 Tage, verlängert sich bei Nutzung)" },
  { label: "Hosting",    value: "Hetzner Cloud, Ubuntu 24.04, Let's Encrypt" },
  { label: "Backup",     value: "Lokal: pg_dump täglich · Offsite: Hetzner Storage Share" },
  { label: "Proxy",      value: "nginx: / → React, /api/ → FastAPI" },
];

// Auto-Auswahl im Scanner (Europe/Berlin), siehe scanner/markets.ts
export const AUTO_WINDOWS = [
  { time: "00:00–08:00", markets: "Asien" },
  { time: "08:00–15:30", markets: "EU · Rohstoffe" },
  { time: "15:30–17:30", markets: "EU · US · Rohstoffe" },
  { time: "17:30–22:00", markets: "US · Rohstoffe" },
  { time: "22:00–24:00", markets: "alle, letzter Stand" },
];
